import L, { type ControlPosition, type Layer, type Path, type PathOptions } from "leaflet";
import type { AreaFeature } from "./apiTypes";
import {
  computeGlance,
  GLANCE_METADATA,
  GLANCE_TIERS,
  matchesFilters,
  type Glance,
  type GlanceCondition,
} from "./glance";
import { glanceStyle, selectedGlanceStyle, tierStyle } from "./statusStyles";

/**
 * R-613: keyless OSM standard tiles (R-460); the tile pane is desaturated in CSS so the tier
 * colours carry the map. (Carto's basemaps now require an API key and were rejected.)
 */
export const BASEMAP_TILE_URL = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
export const BASEMAP_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

export interface MapOptions {
  features: AreaFeature[];
  onSelect?: (feature: AreaFeature) => void;
  phonePadding?: boolean;
  now?: Date;
}

export interface MapPadding {
  paddingTopLeft: [number, number];
  paddingBottomRight: [number, number];
}

export interface MapController {
  selectArea(key: string): void;
  focusArea(key: string): void;
  clearSelection(): void;
  resetView(): void;
  setFeatures(features: AreaFeature[]): void;
  setStatusFilters(filters: Set<GlanceCondition>): void;
  /** Re-evaluate time-based tiers (R-610); restyles in place, never refits. */
  tick(now: Date): void;
  setView(point: [number, number], zoom?: number): void;
  destroy(): void;
}

export const COLLAPSED_SHEET_HEIGHT_PX = 92;

export function focusPadding(phone: boolean, innerHeight: number): MapPadding {
  if (!phone) {
    return { paddingTopLeft: [24, 24], paddingBottomRight: [24, 24] };
  }
  return {
    paddingTopLeft: [24, 96],
    paddingBottomRight: [24, Math.round(innerHeight * 0.5)],
  };
}

export function initialFitPadding(phone: boolean, collapsedSheetHeight: number): MapPadding {
  return {
    paddingTopLeft: [24, 24],
    paddingBottomRight: [24, phone ? collapsedSheetHeight + 24 : 24],
  };
}

function featureKey(feature: AreaFeature): string {
  return feature.properties.key;
}

function glanceForFeature(feature: AreaFeature, now: Date): Glance {
  return computeGlance(feature.properties, now);
}

export function isLowZoom(zoom: number): boolean {
  return zoom < 12;
}

function zoomControlPosition(): ControlPosition {
  const isNarrow = typeof window.matchMedia === "function" && window.matchMedia("(max-width: 767px)").matches;
  return isNarrow ? "bottomright" : "topleft";
}

function optionsForFeature(feature: AreaFeature, selectedKey: string | null, now: Date): PathOptions {
  const glance = glanceForFeature(feature, now);
  return featureKey(feature) === selectedKey
    ? selectedGlanceStyle(glance, feature.properties.stale)
    : glanceStyle(glance, feature.properties.stale);
}

const FILTERED_PATH_STYLE: Pick<PathOptions, "opacity" | "fillOpacity" | "interactive"> = {
  opacity: 0.18,
  fillOpacity: 0.04,
  interactive: false,
};

function applyInteractive(path: Path, interactive: boolean): void {
  path.options.interactive = interactive;
  const element = path.getElement();
  if (!element) {
    return;
  }
  element.classList.toggle("leaflet-interactive", interactive);
}

/** R-674: class list for a polygon path. `is-estimated` is additive to tier/state cues. */
export function pathClassNames(
  feature: AreaFeature,
  selectedKey: string | null,
  filtered: boolean,
  glance: Glance,
): string[] {
  const classes = ["status-polygon", `tier-${glance.tier}`];
  if (glance.possible) {
    classes.push("is-possible");
  }
  if (feature.properties.stale) {
    classes.push("is-stale");
  }
  if (featureKey(feature) === selectedKey) {
    classes.push("is-selected");
  }
  if (filtered) {
    classes.push("is-filtered");
  }
  if (feature.properties.provisional) {
    classes.push("is-estimated");
  }
  return classes;
}

function applyPathClasses(
  layer: Path,
  feature: AreaFeature,
  selectedKey: string | null,
  filtered: boolean,
  glance: Glance,
): void {
  const element = layer.getElement();
  if (!element) {
    return;
  }
  element.classList.remove(
    "status-polygon",
    "is-stale",
    "is-selected",
    "is-filtered",
    "is-possible",
    "is-estimated",
    ...GLANCE_TIERS.map((value) => `tier-${value}`),
  );
  element.classList.add(...pathClassNames(feature, selectedKey, filtered, glance));
  element.setAttribute("data-tier", glance.tier);
  if (feature.properties.stale) {
    element.setAttribute("fill", `url(#veco-stale-${glance.tier})`);
  }
}

export function createMap(root: HTMLElement, options: MapOptions): MapController {
  const leafletMap = L.map(root, {
    attributionControl: true,
    zoomControl: false,
  });
  leafletMap.attributionControl.setPrefix("Unofficial, not affiliated with VECO");
  L.control.zoom({ position: zoomControlPosition() }).addTo(leafletMap);
  L.tileLayer(BASEMAP_TILE_URL, {
    maxZoom: 19,
    attribution: BASEMAP_ATTRIBUTION,
    className: "basemap-tiles",
  }).addTo(leafletMap);
  const phoneLayout = options.phonePadding ?? false;

  let selectedKey: string | null = null;
  let features = options.features;
  let filters = new Set<GlanceCondition>(GLANCE_TIERS);
  let now = options.now ?? new Date();
  let geoJsonLayer: L.GeoJSON | null = null;
  const pathsByKey = new Map<string, Path>();

  function ensureHatchPatterns(): void {
    const svg = root.querySelector("svg");
    if (!svg || svg.querySelector("#veco-stale-patterns")) {
      return;
    }

    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    defs.id = "veco-stale-patterns";
    for (const tier of GLANCE_TIERS) {
      const pattern = document.createElementNS("http://www.w3.org/2000/svg", "pattern");
      pattern.id = `veco-stale-${tier}`;
      pattern.setAttribute("patternUnits", "userSpaceOnUse");
      pattern.setAttribute("width", "10");
      pattern.setAttribute("height", "10");

      const base = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      base.setAttribute("width", "10");
      base.setAttribute("height", "10");
      base.setAttribute("fill", GLANCE_METADATA[tier].color);
      base.setAttribute("fill-opacity", String(tierStyle(tier, false).fillOpacity ?? 0.5));

      const hatch = document.createElementNS("http://www.w3.org/2000/svg", "path");
      hatch.setAttribute("d", "M-2,10 L10,-2 M4,12 L12,4");
      hatch.setAttribute("stroke", "#111827");
      hatch.setAttribute("stroke-width", "2");
      hatch.setAttribute("stroke-opacity", "0.45");

      pattern.appendChild(base);
      pattern.appendChild(hatch);
      defs.appendChild(pattern);
    }
    svg.insertBefore(defs, svg.firstChild);
  }

  function isPath(layer: Layer): layer is Path {
    return typeof (layer as Path).setStyle === "function";
  }

  function featureByKey(key: string): AreaFeature | undefined {
    return features.find((candidate) => featureKey(candidate) === key);
  }

  function isFiltered(feature: AreaFeature, glance = glanceForFeature(feature, now)): boolean {
    return !matchesFilters(glance, filters);
  }

  function restylePath(path: Path, feature: AreaFeature): void {
    const glance = glanceForFeature(feature, now);
    const filtered = isFiltered(feature, glance);
    const style = optionsForFeature(feature, selectedKey, now);
    path.setStyle(filtered ? { ...style, ...FILTERED_PATH_STYLE } : { ...style, interactive: true });
    applyPathClasses(path, feature, selectedKey, filtered, glance);
    applyInteractive(path, !filtered);
  }

  function restyleAll(): void {
    for (const [pathKey, path] of pathsByKey) {
      const feature = featureByKey(pathKey);
      if (!feature) {
        continue;
      }
      restylePath(path, feature);
    }
  }

  function sortedKeys(list: AreaFeature[]): string {
    return list
      .map((feature) => featureKey(feature))
      .sort()
      .join("\0");
  }

  function draw(): void {
    pathsByKey.clear();
    if (geoJsonLayer) {
      geoJsonLayer.removeFrom(leafletMap);
    }

    geoJsonLayer = L.geoJSON(features as never, {
      style(feature) {
        const area = feature as unknown as AreaFeature;
        const style = optionsForFeature(area, selectedKey, now);
        if (isFiltered(area)) {
          return { ...style, ...FILTERED_PATH_STYLE } as PathOptions;
        }
        return style;
      },
      onEachFeature(feature, layer) {
        const area = feature as unknown as AreaFeature;
        const key = featureKey(area);
        if (isPath(layer)) {
          pathsByKey.set(key, layer);
          layer.on("mouseover", () => {
            const current = featureByKey(key);
            if (current && featureKey(current) !== selectedKey && !isFiltered(current)) {
              layer.setStyle({ weight: 4 });
            }
          });
          layer.on("mouseout", () => {
            const current = featureByKey(key);
            if (current) {
              restylePath(layer, current);
            }
          });
          layer.on("click", () => options.onSelect?.(featureByKey(key) ?? area));
        }
      },
    }).addTo(leafletMap);

    ensureHatchPatterns();
    geoJsonLayer.eachLayer((layer) => {
      const area = (layer as Layer & { feature?: AreaFeature }).feature;
      if (area && isPath(layer)) {
        const glance = glanceForFeature(area, now);
        const filtered = isFiltered(area, glance);
        applyPathClasses(layer, area, selectedKey, filtered, glance);
        applyInteractive(layer, !filtered);
      }
    });
  }

  function syncLowZoomClass(): void {
    root.classList.toggle("is-low-zoom", isLowZoom(leafletMap.getZoom()));
  }

  function fitAll(): void {
    if (!geoJsonLayer) {
      return;
    }
    const bounds = geoJsonLayer.getBounds();
    if (bounds.isValid()) {
      leafletMap.fitBounds(bounds, initialFitPadding(phoneLayout, COLLAPSED_SHEET_HEIGHT_PX));
    }
  }

  function fitArea(key: string): void {
    const path = pathsByKey.get(key) as (Path & { getBounds?: () => L.LatLngBounds }) | undefined;
    const bounds = path?.getBounds?.();
    if (!bounds?.isValid()) {
      return;
    }
    leafletMap.fitBounds(bounds, {
      maxZoom: 15,
      ...focusPadding(phoneLayout, window.innerHeight),
    });
  }

  draw();
  fitAll();
  leafletMap.on("zoomend", syncLowZoomClass);
  syncLowZoomClass();

  return {
    selectArea(key: string) {
      selectedKey = key;
      restyleAll();
    },
    focusArea(key: string) {
      selectedKey = key;
      restyleAll();
      fitArea(key);
    },
    clearSelection() {
      selectedKey = null;
      restyleAll();
    },
    resetView() {
      fitAll();
    },
    setFeatures(nextFeatures: AreaFeature[]) {
      const sameKeys = sortedKeys(features) === sortedKeys(nextFeatures);
      features = nextFeatures;
      if (sameKeys) {
        restyleAll();
        return;
      }
      draw();
    },
    setStatusFilters(nextFilters: Set<GlanceCondition>) {
      filters = nextFilters;
      restyleAll();
    },
    tick(nextNow: Date) {
      now = nextNow;
      restyleAll();
    },
    setView(point: [number, number], zoom = 15) {
      leafletMap.setView(point, zoom);
    },
    destroy() {
      leafletMap.remove();
    },
  };
}
